import {
  AUTH_ROUTES,
  CRUMB_KEYS,
  KNOWN_HEADS,
  LOCK_ROUTE,
  LOGIN_ROUTE,
  MARKETING_ROUTES,
  REPORTS_SUB_KEYS,
  SETTINGS_SUB_KEYS,
} from '@constants/routes'

/**
 * ルート文字列に関する純粋関数群。React に依存しないので
 * node 環境のテストで完全網羅できる (jsdom 不要)。
 */

/** Guard が下す判断: そのまま通す or 指定先に redirect する。 */
export type GuardAction = { kind: 'allow' } | { kind: 'redirect'; to: string }

/**
 * `'#vault/folder/abc'` → `'vault/folder/abc'`。空 / `'#'` のみ → `'landing'`。
 *
 * T-M 以降、root URL `/` (= no hash) は marketing landing page に着地させる
 * (旧仕様は `'home'` だが認証必須 → unauth user が即 login に弾かれる UX を解消)。
 * 認証済 user が `/` から home へ行きたい場合は Sidenav の Home / 直接 `#home`。
 */
export const parseRoute = (hash: string): string => {
  const raw = hash.replace(/^#/, '').trim()

  return raw.length > 0 ? raw : 'landing'
}

/**
 * AUTH_ROUTES に含まれているか (split-screen / 未認証可)。
 * `verify-email/<token>` は動的セグメントを持つので、prefix 一致でも auth route として許可する。
 */
export const isAuthRoute = (route: string): boolean => {
  if (AUTH_ROUTES.has(route)) return true
  if (route.startsWith('verify-email/')) return true

  return false
}

/**
 * MARKETING_ROUTES に含まれているか (T-M、Landing/Docs/Contact、unauth でも閲覧可)。
 */
export const isMarketingRoute = (route: string): boolean => MARKETING_ROUTES.has(route)

/**
 * 認証不要で誰でも閲覧できる route (AUTH + MARKETING の合算)。
 * Guard / Layout 切替時の判断に使う。
 */
export const isPublicRoute = (route: string): boolean =>
  isAuthRoute(route) || isMarketingRoute(route)

/**
 * 未知のルートを `'home'` にフォールバックする。
 *   - AUTH_ROUTES 完全一致 → そのまま (login / register / forgot/* / lock / terms / privacy)
 *   - MARKETING_ROUTES 完全一致 → そのまま (T-M: landing / docs / contact)
 *   - head が KNOWN_HEADS → そのまま (子セグメントの妥当性は各 screen で検証)
 *   - それ以外 → `'home'`
 */
export const resolveRoute = (route: string): string => {
  if (isPublicRoute(route)) return route
  const head = route.split('/')[0] ?? ''
  if (KNOWN_HEADS.has(head)) return route

  return 'home'
}

/** `parseRoute → resolveRoute` の合成。hashchange handler から直接呼べる。 */
export const handleHashChange = (hash: string): string => resolveRoute(parseRoute(hash))

/**
 * パンくずを **i18n key の配列** として返す (固定セグメントのみ key、動的セグメントは
 * 生 ID をそのまま含める)。描画側で `crumbsFor(route).map((seg) => t(seg) || seg)` のように
 * 変換し、key として解決できないものは生文字列としてそのまま表示する想定。
 *
 * Vault の動的セグメント (folder.name / item.name 等) は Phase 3 後半でデータ層が
 * 揃ってから名前解決に差し替える。それまでは ID をそのまま見せる。
 */
export const crumbsFor = (route: string): readonly string[] => {
  const direct = CRUMB_KEYS[route]
  if (direct) return direct

  if (route.startsWith('vault/folder/')) {
    const id = route.split('/')[2] ?? ''

    return ['common.nav.vault', id || 'common.nav.default_folder']
  }
  if (route.startsWith('vault/') && route.endsWith('/edit')) {
    return ['common.nav.vault', 'common.nav.vault_edit']
  }
  if (route.startsWith('vault/')) {
    const id = route.split('/')[1] ?? ''

    return ['common.nav.vault', id || 'common.nav.default_item']
  }
  if (route.startsWith('reports/')) {
    const sub = route.split('/')[1] ?? ''

    return ['common.nav.reports', REPORTS_SUB_KEYS[sub] ?? sub]
  }
  if (route.startsWith('settings/')) {
    const sub = route.split('/')[1] ?? ''

    return ['common.nav.settings', SETTINGS_SUB_KEYS[sub] ?? '']
  }
  if (route.startsWith('verify-email/')) {
    return ['common.nav.verify_email']
  }
  // Track F: 緊急アクセスの保管庫閲覧。設定 → 緊急アクセス から到達する。
  if (route.startsWith('emergency/vault/')) {
    return ['common.nav.settings', 'common.settings_sub.emergency']
  }

  return [route]
}

/**
 * 認証状態とルートから Guard 判断を導出する。
 *
 * 優先順位:
 *   1. AUTH_ROUTES (login / lock / forgot/* / terms / privacy) は常に allow
 *   2. LOCKED 状態で保護ルート → `#lock` (アイドルロック後の直接 URL 入力で
 *      `#login` に飛ばさず、unlock フォームに誘導する。L-18a)
 *   3. 未認証で保護ルート → `#login`
 *   4. それ以外 → allow
 */
export const computeGuardAction = (
  route: string,
  isAuthenticated: boolean,
  isLocked: boolean,
): GuardAction => {
  // T-M: Marketing routes も AUTH_ROUTES と同じく public (誰でも閲覧可)。
  if (isPublicRoute(route)) return { kind: 'allow' }
  if (isLocked) return { kind: 'redirect', to: LOCK_ROUTE }
  if (!isAuthenticated) return { kind: 'redirect', to: LOGIN_ROUTE }

  return { kind: 'allow' }
}

/**
 * ハッシュナビゲーション。
 * `target` は先頭 `'#'` 付きでも無くても受け付ける。同じ hash に navigate しても
 * hashchange は発火しないため、無理に dispatchEvent はしない。
 */
export const navigate = (target: string): void => {
  if (typeof window === 'undefined') return
  const next = target.startsWith('#') ? target : `#${target}`
  if (window.location.hash === next) return
  window.location.hash = next
}

/**
 * ロゴ等の「トップページ (/) へ戻る」遷移。root `/` (= 空 hash → landing) に
 * **same-document** で戻す。full reload しないので、ログイン中でも in-memory
 * session を失わない。
 *
 * `window.location.hash = ''` の代入だけでは、一部ブラウザで hashchange が
 * 発火せず useHashRoute が更新されない (= ロゴを押しても無反応) ため、
 * History API で `/` (hash 無し) に push したうえで hashchange を明示的に発火させる。
 */
export const navigateHome = (): void => {
  if (typeof window === 'undefined') return
  const { pathname, search, hash } = window.location
  if (hash === '' || hash === '#') return
  window.history.pushState(null, '', pathname + search)
  window.dispatchEvent(new HashChangeEvent('hashchange'))
}
